import { TaskType } from '@api';
import { Box, Group, Indicator, Stack } from '@mantine/core';
import {
  TaskClaimAction,
  TaskClaimerAvatar,
  TaskCloseAction,
  TaskCreatedAt,
  TaskDescription,
  TaskOpenContextAction,
  TaskPriority,
  TaskTitle,
  TaskUndoAction,
} from './components';
import { isClaimActionVisible, isMarkReadEligible } from './helpers';
import styles from './TaskItem.module.css';

type TaskItemPropsType = {
  task: TaskType;
  currentUserId: string;
  onMarkAsRead: (taskId: string) => void;
  onClaim: (taskId: string) => void;
  onUnclaim: (taskId: string) => void;
  onClose: (taskId: string) => void;
  onReopen: (taskId: string) => void;
  onView: (task: TaskType) => void;
};

export const TaskItem: React.FC<TaskItemPropsType> = ({
  task,
  currentUserId,
  onMarkAsRead,
  onClaim,
  onUnclaim,
  onClose,
  onReopen,
  onView,
}) => {
  const isTaskUnread = isMarkReadEligible(task);
  const isOpen = task.status === 'OPEN';
  const isClosed = !isOpen;
  const showClaim = isClaimActionVisible({
    status: task.status,
    claimedById: task.claimedBy?.id ?? null,
    currentUserId,
  });
  const isUnclaim = task.claimedBy?.id === currentUserId;

  const handleMouseEnter = (): void => {
    if (!isTaskUnread) return;

    onMarkAsRead(task.id);
  };

  return (
    <Indicator
      disabled={!isTaskUnread}
      size={8}
      color="blue"
      position="top-start"
    >
      <Box
        p="sm"
        bg="layout.11"
        className={`${styles.root}${isClosed ? ` ${styles.closed}` : ''}`}
        onMouseEnter={handleMouseEnter}
      >
        <Stack gap="xs" miw={0}>
          <Group gap="xs" align="flex-start" wrap="nowrap" miw={0}>
            <TaskPriority priority={task.priority} />
            <TaskTitle
              title={task.title}
              isClosed={isClosed}
              onClick={() => onView(task)}
            />
          </Group>
          <TaskDescription description={task.description} isClosed={isClosed} />
          <Group justify="space-between" align="center" wrap="nowrap" miw={0}>
            <Group gap="xs" align="center" wrap="nowrap" miw={0}>
              <TaskClaimerAvatar claimedBy={task.claimedBy} />
              <TaskCreatedAt createdAt={task.createdAt} />
            </Group>
            <Group gap={2}>
              {isOpen ? (
                <>
                  {showClaim ? (
                    <TaskClaimAction
                      isUnclaim={isUnclaim}
                      onClick={() =>
                        isUnclaim ? onUnclaim(task.id) : onClaim(task.id)
                      }
                    />
                  ) : null}
                  {isOpen && task.context !== null ? (
                    <TaskOpenContextAction to={task.context} />
                  ) : null}
                  <TaskCloseAction onClose={() => onClose(task.id)} />
                </>
              ) : (
                <TaskUndoAction onReopen={() => onReopen(task.id)} />
              )}
            </Group>
          </Group>
        </Stack>
      </Box>
    </Indicator>
  );
};
